import React, { useCallback, useState } from 'react';

import { closestCenter, DndContext, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import cx from 'classnames';
import { always, isEmpty } from 'ramda';

import { useAssetsHandlers } from 'components/Pages/Admin/GuestPortal/shared/functions';
import AddMinusButton from 'components/shared/AddMinusButton';
import { SortableWrapper, SortableWrapperLayout } from 'components/shared/SortableWrapper';
import Text from 'components/shared/Typography/Text';
import { FILE_MIME_TYPES } from 'utils/asset';

import FileItem from './FileItem';
import styles from './index.module.css';

import Upload from './index';

const fileHasURL = (enteredFile) => Boolean(enteredFile?.url);

function FilesAdapter({
  value,
  onChange,
  fileCategory,
  maxFileSize,
  recommendedFileSize,
  withDnd = false,
  withDivider = false,
  withEmpty = true,
  renderHeader = undefined,
  renderEmptyComponent,
  contentWrapperClassName,
  addActionDisabled,
  customStyleKey,
  uploadAlertText,
  modifiers = [],
}) {
  const isDndActive = withDnd && value?.length > 1;

  const { handleDeleteAsset, handleEditAsset, handleUploadAssets } = useAssetsHandlers();
  const onUpload = () => {
    if (!fileCategory) return;
    handleUploadAssets({ value, onChange, category: fileCategory, infoAlertText: uploadAlertText });
  };
  const onDelete = (asset) => fileHasURL(asset) && handleDeleteAsset(asset, { value, onChange });
  const onEdit = (asset) => fileHasURL(asset) && handleEditAsset(asset.id, { value, onChange });

  const [draggingItemId, setDraggingItemId] = useState(null);

  const renderDruggingOverlay = useCallback(() => {
    if (!draggingItemId) return null;

    const druggingItem = value.find((file) => file.id === draggingItemId);
    const index = value.findIndex((file) => file.id === draggingItemId);
    if (!druggingItem) return null;

    return (
      <DragOverlay adjustScale={false} modifiers={modifiers}>
        <SortableWrapperLayout withDnd={isDndActive} withDivider={withDivider}>
          <div className={styles.sheetsItem}>
            <Text type="brandon" isStrong>
              {index + 1}.
            </Text>
            <FileItem
              itemId={draggingItemId}
              isActionsDisabled={!fileHasURL(druggingItem)}
              onEdit={always(null)}
              onDelete={always(null)}
              customStyleKey={customStyleKey}
            >
              {druggingItem.name}
            </FileItem>
          </div>
        </SortableWrapperLayout>
      </DragOverlay>
    );
  }, [draggingItemId, value]);

  const handleDragEnd = (event) => {
    document.body.classList.remove('dragging');
    const { active, over } = event;
    if (!(active && over)) {
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = value.map((e) => e.id).indexOf(active.id);
      const newIndex = value.map((e) => e.id).indexOf(over?.id);

      const movedFiles = arrayMove(value, oldIndex, newIndex);
      const filesWithPositions = movedFiles.map((file, index) => ({ ...file, position: index }));

      onChange(filesWithPositions);
    }
  };

  if (withEmpty && isEmpty(value)) {
    if (renderEmptyComponent) {
      return renderEmptyComponent(onUpload);
    }

    return (
      <Upload
        onOpenModal={onUpload}
        allowedMimeTypes={FILE_MIME_TYPES}
        maxFileSize={maxFileSize}
        recommendedFileSize={recommendedFileSize}
        buttonActionType="add"
      />
    );
  }

  return (
    <>
      {renderHeader && renderHeader(onUpload)}

      <div
        className={cx(styles.sheetsWrapper, contentWrapperClassName, {
          [`${customStyleKey}-list-wrapper`]: customStyleKey,
        })}
      >
        <DndContext
          collisionDetection={closestCenter}
          onDragStart={({ active }) => {
            if (!active) {
              return;
            }
            document.body.classList.add('dragging');
            setDraggingItemId(active.id);
          }}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            document.body.classList.remove('dragging');
            setDraggingItemId(null);
          }}
        >
          <SortableContext items={value} strategy={verticalListSortingStrategy}>
            <div
              className={cx(
                styles.sheets,
                { [styles.noBottomBorder]: withDivider },
                { [`${customStyleKey}-list`]: customStyleKey },
              )}
            >
              {value.map((file, index) => (
                <SortableWrapper key={file.id} id={file.id} withDnd={isDndActive} withDivider={withDivider}>
                  <div className={styles.sheetsItem}>
                    <Text type="brandon" isStrong>
                      {index + 1}.
                    </Text>
                    <FileItem
                      itemId={file.id}
                      isActionsDisabled={!fileHasURL(file)}
                      onEdit={() => onEdit(file)}
                      onDelete={() => onDelete(file)}
                      customStyleKey={customStyleKey}
                    >
                      {file.name}
                    </FileItem>
                  </div>
                </SortableWrapper>
              ))}
            </div>

            {!addActionDisabled && (
              <AddMinusButton onClick={onUpload} />
              // <AddMinusButton className={styles.addBtn} onClick={onUpload} iconComponent={CirclePlusIconThin} />
            )}

            {renderDruggingOverlay()}
          </SortableContext>
        </DndContext>
      </div>
    </>
  );
}

export { FilesAdapter };
export default FilesAdapter;
